/**
 * VB Server - Messages API
 *
 * Message queries and updates (ratings, text corrections, tags).
 *
 * @example
 * import { listMessages, getMessage, updateMessage } from '@/api/server/messages';
 *
 * const messages = await listMessages({ orgId, channelIds });
 * await updateMessage(messageId, orgId, { transcription_accuracy: 4 });
 */

import {
  serverGet,
  serverGetFull,
  serverPatch,
  serverPost,
  APIError,
  getServerUrl,
  getJWT
} from './client';
import { logger } from '../../utils/logger';

// =============================================================================
// Types
// =============================================================================

/**
 * Message entity from vb-server
 */
export interface Message {
  talk_id: string;
  org_id: string;
  channel_id: string;
  device_id?: string | null;
  text: string;
  speaker_id?: string | null;
  tags?: string[];
  confidence?: number;
  duration?: number;
  transcription_accuracy?: number | null;
  audio_quality?: number | null;
  source_type?: string;
  feed_id?: string | null;
  // Broadcastify Calls metadata
  radio_id?: string | null;
  talkgroup?: string | null;
  talkgroup_tag?: string | null;
  radio_tag?: string | null;
  frequency?: number | null;
  system_id?: number | null;
  emergency?: boolean;
  encrypted?: boolean;
  // ICY stream metadata
  icy_name?: string | null;
  icy_description?: string | null;
  // RingCentral call metadata
  rc_call_id?: string | null;
  rc_session_id?: string | null;
  direction?: string | null;
  call_from?: string | null;
  call_to?: string | null;
  extension?: string | null;
  created: string;
  modified: string;
}

/**
 * Options for listing messages
 */
export interface ListMessagesOptions {
  orgId: string;
  channelIds?: string[];
  sourceTypes?: string[];
  deviceIds?: string[];
  feedIds?: string[];
  // Broadcastify Calls metadata filters
  talkgroups?: string[];
  talkgroupTags?: string[];
  radioIds?: string[];
  emergencyOnly?: boolean;
  // User-assigned tags filter
  tags?: string[];
  // Speaker filters
  speakerIds?: string[];
  excludeSpeakerIds?: string[];
  // Exclude filters
  excludeChannelIds?: string[];
  excludeSourceTypes?: string[];
  excludeDeviceIds?: string[];
  excludeTalkgroupTags?: string[];
  excludeRadioIds?: string[];
  excludeTags?: string[];
  before?: string | number;
  after?: string | number;
  order?: 'asc' | 'desc';
  limit?: number;
}

/**
 * Options for searching messages
 */
export interface SearchMessagesOptions {
  orgId: string;
  query: string;
  channelIds?: string[];
  sourceTypes?: string[];
  deviceIds?: string[];
  startTime?: string | number;
  endTime?: string | number;
  limit?: number;
}

/**
 * Input for updating a message
 */
export interface UpdateMessageInput {
  text?: string;
  tags?: string[];
  transcription_accuracy?: number | null;
  audio_quality?: number | null;
  speaker_id?: string | null;
}

/**
 * Input for sending a message to a channel
 * Either text (for TTS) or audio (base64) is required, but not both
 */
export interface SendMessageInput {
  channel_id: string;
  text?: string; // Text for TTS (max 5000 chars)
  audio?: string; // Base64 data URL (e.g., data:audio/mpeg;base64,...)
  voice?: string; // TTS voice ID (e.g., 'Joanna')
  locale?: string; // e.g., 'en-US'
}

/**
 * Response from sending a message
 */
export interface SendMessageResponse {
  success: boolean;
  message: Message;
}

/**
 * List messages response
 */
export interface ListMessagesResponse {
  messages: Message[];
  total: number;
  hasMore: boolean;
  pagination?: {
    limit: number;
    before?: string | null;
    after?: string | null;
  };
}

/**
 * A single facet value with its occurrence count
 */
export interface FacetValue {
  value: string;
  count: number;
}

/**
 * Response from the facets endpoint
 */
export interface FacetResponse {
  field: string;
  values: FacetValue[];
}

/**
 * Options for fetching facet values
 */
export interface GetFacetsOptions {
  orgId: string;
  field:
    | 'channel_id'
    | 'source_type'
    | 'device_id'
    | 'talkgroup_tag'
    | 'radio_id'
    | 'tag'
    | 'speaker_id'
    | 'feed_id';
  channelIds?: string[];
  start?: string;
  end?: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List messages with pagination
 *
 * @param options - Query options
 * @returns Messages and pagination info
 */
export async function listMessages(
  options: ListMessagesOptions,
  signal?: AbortSignal
): Promise<ListMessagesResponse> {
  const params = new URLSearchParams();
  params.set('org_id', options.orgId);

  if (options.channelIds?.length) {
    params.set('channel_ids', options.channelIds.join(','));
  }
  if (options.sourceTypes?.length) {
    params.set('source_types', options.sourceTypes.join(','));
  }
  if (options.deviceIds?.length) {
    params.set('device_ids', options.deviceIds.join(','));
  }
  if (options.feedIds?.length) {
    params.set('feed_ids', options.feedIds.join(','));
  }
  // Broadcastify Calls metadata filters
  if (options.talkgroups?.length) {
    params.set('talkgroups', options.talkgroups.join(','));
  }
  if (options.talkgroupTags?.length) {
    params.set('talkgroup_tags', options.talkgroupTags.join(','));
  }
  if (options.radioIds?.length) {
    params.set('radio_ids', options.radioIds.join(','));
  }
  if (options.emergencyOnly) {
    params.set('emergency_only', 'true');
  }
  // User-assigned tags filter
  if (options.tags?.length) {
    params.set('tags', options.tags.join(','));
  }
  // Exclude filters
  if (options.excludeChannelIds?.length) {
    params.set('exclude_channel_ids', options.excludeChannelIds.join(','));
  }
  if (options.excludeSourceTypes?.length) {
    params.set('exclude_source_types', options.excludeSourceTypes.join(','));
  }
  if (options.excludeDeviceIds?.length) {
    params.set('exclude_device_ids', options.excludeDeviceIds.join(','));
  }
  if (options.excludeTalkgroupTags?.length) {
    params.set('exclude_talkgroup_tags', options.excludeTalkgroupTags.join(','));
  }
  if (options.excludeRadioIds?.length) {
    params.set('exclude_radio_ids', options.excludeRadioIds.join(','));
  }
  if (options.excludeTags?.length) {
    params.set('exclude_tags', options.excludeTags.join(','));
  }
  // Speaker filters
  if (options.speakerIds?.length) {
    params.set('speaker_ids', options.speakerIds.join(','));
  }
  if (options.excludeSpeakerIds?.length) {
    params.set('exclude_speaker_ids', options.excludeSpeakerIds.join(','));
  }
  if (options.before) {
    params.set('before', String(options.before));
  }
  if (options.after) {
    params.set('after', String(options.after));
  }
  if (options.order) {
    params.set('order', options.order);
  }
  if (options.limit) {
    params.set('limit', String(options.limit));
  }

  const url = `/messages?${params.toString()}`;
  logger.info('[listMessages] Requesting:', url);

  // Use serverGetFull to preserve total, hasMore, pagination metadata
  const response = await serverGetFull<Message[]>(url, signal);

  logger.info('[listMessages] Response:', {
    dataLength: response.data?.length,
    total: response.total,
    hasMore: response.hasMore,
    pagination: response.pagination
  });

  return {
    messages: response.data || [],
    total: response.total || response.data?.length || 0,
    hasMore: response.hasMore || false,
    pagination: response.pagination
  };
}

/**
 * Get distinct values for a metadata field with occurrence counts.
 *
 * @param options - Facet query options (orgId, field, optional scoping)
 * @returns Field name and array of { value, count } pairs
 */
export async function getFacets(options: GetFacetsOptions): Promise<FacetResponse> {
  const params = new URLSearchParams();
  params.set('org_id', options.orgId);
  params.set('field', options.field);

  if (options.channelIds?.length) {
    params.set('channel_ids', options.channelIds.join(','));
  }
  if (options.start) {
    params.set('start', options.start);
  }
  if (options.end) {
    params.set('end', options.end);
  }

  const url = `/messages/facets?${params.toString()}`;
  logger.info('[getFacets] Requesting:', url);

  const response = await serverGet<FacetResponse>(url);
  return response;
}

/**
 * Get a single message by ID
 *
 * @param messageId - Message/talk ID
 * @returns Message or null if not found
 */
export async function getMessage(messageId: string, orgId?: string): Promise<Message | null> {
  try {
    const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
    return await serverGet<Message>(`/messages/${messageId}${qs}`);
  } catch (error) {
    if (error instanceof APIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Update a message (ratings, text, tags, speaker)
 *
 * @param messageId - Message/talk ID
 * @param orgId - Organization ID (required for access validation)
 * @param data - Fields to update
 * @returns Updated message
 */
export async function updateMessage(
  messageId: string,
  orgId: string,
  data: UpdateMessageInput
): Promise<Message> {
  return serverPatch<Message>(`/messages/${messageId}?org_id=${encodeURIComponent(orgId)}`, data);
}

/**
 * Rate message transcription accuracy (0-5)
 *
 * @param messageId - Message/talk ID
 * @param orgId - Organization ID
 * @param rating - Accuracy rating 0-5
 * @returns Updated message
 */
export async function rateTranscriptionAccuracy(
  messageId: string,
  orgId: string,
  rating: number | null
): Promise<Message> {
  return updateMessage(messageId, orgId, { transcription_accuracy: rating });
}

/**
 * Rate message audio quality (0-5)
 *
 * @param messageId - Message/talk ID
 * @param orgId - Organization ID
 * @param rating - Quality rating 0-5
 * @returns Updated message
 */
export async function rateAudioQuality(
  messageId: string,
  orgId: string,
  rating: number | null
): Promise<Message> {
  return updateMessage(messageId, orgId, { audio_quality: rating });
}

/**
 * Update message text (transcript correction)
 *
 * @param messageId - Message/talk ID
 * @param orgId - Organization ID
 * @param text - Corrected text
 * @returns Updated message
 */
export async function updateMessageText(
  messageId: string,
  orgId: string,
  text: string
): Promise<Message> {
  return updateMessage(messageId, orgId, { text });
}

/**
 * Update message tags
 *
 * @param messageId - Message/talk ID
 * @param orgId - Organization ID
 * @param tags - Array of tag names
 * @returns Updated message
 */
export async function updateMessageTags(
  messageId: string,
  orgId: string,
  tags: string[]
): Promise<Message> {
  return updateMessage(messageId, orgId, { tags });
}

/**
 * Assign speaker to message
 *
 * @param messageId - Message/talk ID
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID to assign (or null to remove)
 * @returns Updated message
 */
export async function assignSpeaker(
  messageId: string,
  orgId: string,
  speakerId: string | null
): Promise<Message> {
  return updateMessage(messageId, orgId, { speaker_id: speakerId });
}

// =============================================================================
// Audio Functions
// =============================================================================

/**
 * Get the audio URL for a message
 * Returns a URL that can be used in an <audio> element
 *
 * @param messageId - Message/talk ID
 * @param orgId - Optional org ID for access validation
 * @returns URL to the audio endpoint
 */
export function getMessageAudioUrl(messageId: string, orgId?: string): string {
  const baseUrl = getServerUrl();
  const params = new URLSearchParams();
  if (orgId) {
    params.set('org_id', orgId);
  }
  const queryString = params.toString();
  return `${baseUrl}/messages/${messageId}/audio${queryString ? `?${queryString}` : ''}`;
}

/**
 * Get audio blob for a message
 * Fetches the audio file as a Blob for local playback
 *
 * In local development, the server will proxy audio requests to production
 * if the audio file doesn't exist locally.
 *
 * @param messageId - Message/talk ID
 * @param orgId - Optional org ID for access validation
 * @returns Audio blob
 */
export async function getMessageAudio(messageId: string, orgId?: string): Promise<Blob> {
  const baseUrl = getServerUrl();
  const params = new URLSearchParams();
  if (orgId) {
    params.set('org_id', orgId);
  }
  const queryString = params.toString();
  const url = `${baseUrl}/messages/${messageId}/audio${queryString ? `?${queryString}` : ''}`;

  const token = getJWT();
  const headers: Record<string, string> = {};
  if (token && token !== 'cookie-auth') {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}

// =============================================================================
// Send Functions
// =============================================================================

/**
 * Send a message to a channel
 * Supports text-to-speech or audio file upload
 *
 * @param input - Message input (channel_id required, plus text OR audio)
 * @returns Sent message data
 *
 * @example
 * // Send TTS message
 * await sendMessage({ channel_id: 'ch123', text: 'Hello everyone!' });
 *
 * @example
 * // Send audio file (from recorder or file upload)
 * const audioDataUrl = 'data:audio/mpeg;base64,...';
 * await sendMessage({ channel_id: 'ch123', audio: audioDataUrl });
 */
export async function sendMessage(input: SendMessageInput): Promise<SendMessageResponse> {
  return serverPost<SendMessageResponse>('/messages/send', input);
}

/**
 * Generate a public share link for a message.
 * Returns a URL with an HMAC-signed token that grants temporary public access.
 */
export async function shareMessage(
  messageId: string,
  orgId: string
): Promise<{ url: string; pid: string }> {
  return serverPost<{ url: string; pid: string }>(
    `/messages/${messageId}/share?org_id=${encodeURIComponent(orgId)}`,
    {}
  );
}
